import React, { useRef, useState, useEffect } from 'react';
import { Download, Eye, Loader, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// Agreement Preview Card - SIMPLE VERSION
// Only shows the saved PDF from backend
// No regeneration, no complex logic
export default function AgreementPreviewCard({ agreement, rental, onDownload }) {
  const [canPreview, setCanPreview] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const previewRef = useRef(null);

  if (!agreement || !rental) return null;

  // Fetch and display saved PDF when preview button clicked
  useEffect(() => {
    if (!canPreview || pdfDataUrl) return;

    const fetchPdf = async () => {
      setIsLoadingPdf(true);
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'https://api.orello.in/api';
        const token = localStorage.getItem('orello_token');

        console.log(`📥 Fetching saved PDF for agreement: ${agreement.id}`);

        const response = await fetch(`${API_URL}/agreements/${agreement.id}/pdf`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch PDF (${response.status})`);
        }

        const blob = await response.blob();
        const dataUrl = URL.createObjectURL(blob);
        setPdfDataUrl(dataUrl);

        console.log('✅ PDF loaded from backend');
      } catch (error) {
        console.error('❌ PDF fetch error:', error);
        toast.error('Failed to load PDF');
      } finally {
        setIsLoadingPdf(false);
      }
    };

    fetchPdf();
  }, [canPreview, agreement.id]);

  // Re-fetch the latest PDF (e.g. after a party signs and the signed version updates).
  // Cache-busting query param avoids serving a stale cached copy.
  const refreshPreview = async () => {
    if (pdfDataUrl) URL.revokeObjectURL(pdfDataUrl);
    setPdfDataUrl(null);
    setIsLoadingPdf(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.orello.in/api';
      const token = localStorage.getItem('orello_token');
      const response = await fetch(`${API_URL}/agreements/${agreement.id}/pdf?t=${Date.now()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`Failed to fetch PDF (${response.status})`);
      const blob = await response.blob();
      setPdfDataUrl(URL.createObjectURL(blob));
      toast.success('Preview refreshed');
    } catch (error) {
      console.error('❌ PDF refresh error:', error);
      toast.error('Failed to refresh PDF');
    } finally {
      setIsLoadingPdf(false);
    }
  };

  // Check signature status
  const allTenantsSigned = agreement.tenant_signatures?.every(sig => sig.signed_at);
  const landlordSigned = agreement.signed_by_landlord;
  const allSigned = allTenantsSigned && landlordSigned;

  // PDF can always be generated from stored data
  const hasPDF = true;

  // Signature status message
  const getSignatureStatus = () => {
    if (allSigned) return { status: '✅ Fully Signed', color: 'text-emerald-700', bg: 'bg-emerald-50' };
    if (allTenantsSigned && !landlordSigned) return { status: '⏳ Awaiting Owner Signature', color: 'text-amber-700', bg: 'bg-amber-50' };
    if (landlordSigned && !allTenantsSigned) return { status: '⏳ Awaiting Tenant Signature', color: 'text-amber-700', bg: 'bg-amber-50' };
    return { status: '⏳ Awaiting Signatures', color: 'text-amber-700', bg: 'bg-amber-50' };
  };

  const signStatus = getSignatureStatus();

  const handlePreview = () => {
    if (canPreview) {
      // Clear data URL when closing
      if (pdfDataUrl) {
        URL.revokeObjectURL(pdfDataUrl);
        setPdfDataUrl(null);
      }
    }
    setCanPreview(!canPreview);
  };

  const handleDownload = async () => {
    try {
      setIsLoadingPdf(true);

      const API_URL = process.env.REACT_APP_API_URL || 'https://api.orello.in/api';

      // Fetch saved PDF blob from backend
      const response = await fetch(`${API_URL}/agreements/${agreement.id}/pdf`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('orello_token')}` }
      });

      if (!response.ok) throw new Error(`Failed to fetch PDF`);

      const blob = await response.blob();

      // Download the PDF
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `agreement_${agreement.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('✅ PDF downloaded!');
      onDownload?.();
    } catch (error) {
      console.error('❌ Download error:', error);
      toast.error('Failed to download PDF');
    } finally {
      setIsLoadingPdf(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Signature Status Badge */}
      <div className={`rounded-lg p-4 flex items-center justify-between ${signStatus.bg}`}>
        <div className={`text-sm font-semibold ${signStatus.color}`}>
          {signStatus.status}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handlePreview}
            disabled={!hasPDF}
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${
              hasPDF
                ? 'text-blue-600 hover:text-blue-700 border border-blue-200 hover:border-blue-300'
                : 'text-gray-400 border border-gray-200 cursor-not-allowed'
            }`}
          >
            <Eye className="h-3.5 w-3.5" />
            {canPreview ? 'Hide' : 'Preview'}
          </button>
          {canPreview && (
            <button
              onClick={refreshPreview}
              disabled={isLoadingPdf}
              title="Reload the latest signed version"
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${
                !isLoadingPdf
                  ? 'text-blue-600 hover:text-blue-700 border border-blue-200 hover:border-blue-300'
                  : 'text-gray-400 border border-gray-200 cursor-not-allowed'
              }`}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoadingPdf ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          )}
          <button
            onClick={handleDownload}
            disabled={isLoadingPdf}
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${
              !isLoadingPdf
                ? 'text-green-600 hover:text-green-700 border border-green-200 hover:border-green-300'
                : 'text-gray-400 border border-gray-200 cursor-not-allowed'
            }`}
          >
            <Download className="h-3.5 w-3.5" />
            {isLoadingPdf ? 'Loading...' : 'Download'}
          </button>
        </div>
      </div>

      {/* PDF Preview */}
      {canPreview && (
        <div ref={previewRef} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
          {isLoadingPdf ? (
            <div className="w-full h-96 bg-white flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <Loader className="h-8 w-8 animate-spin text-blue-600" />
                <p className="text-sm text-gray-600">Loading PDF...</p>
              </div>
            </div>
          ) : pdfDataUrl ? (
            <iframe
              src={pdfDataUrl}
              className="w-full h-96 bg-white"
              title="Agreement PDF Preview"
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
